import { groupBy } from 'lodash-es'

import type { Entity as StudentEntity } from '../Student/dto'
import type { ConceptChipApi } from './api'
import { ACHIEVEMENT_LEVEL_KO_TEXT_DICT } from './constants'
import type { ConceptChipDomain } from './domain'
import type { ConceptChipALevel, ConceptChipDLevel } from './types'

export namespace Entity {
  export class ConceptChip {
    readonly conceptChipId: number
    readonly conceptId: number
    readonly levelOfAchievement: ConceptChipALevel
    readonly levelOfConceptChip: ConceptChipDLevel
    readonly conceptName: string
    readonly littleChapterId: number

    constructor(data: {
      conceptChipId: number
      conceptId: number
      levelOfAchievement: ConceptChipALevel
      levelOfConceptChip: ConceptChipDLevel
      conceptName: string
      littleChapterId: number
    }) {
      this.conceptChipId = data.conceptChipId
      this.conceptId = data.conceptId
      this.levelOfAchievement = data.levelOfAchievement
      this.levelOfConceptChip = data.levelOfConceptChip
      this.conceptName = data.conceptName
      this.littleChapterId = data.littleChapterId
    }
  }

  export class AchievementConceptChip {
    id: ConceptChipApi.Response.ConceptChip['conceptChipId']

    orderingNumber: ConceptChipApi.Response.ConceptChip['orderingNumber'] // 정렬 기준 번호 (conceptChip)
    conceptChipId: ConceptChipApi.Response.ConceptChip['conceptChipId'] // 유형칩id
    conceptChipType: ConceptChipApi.Response.ConceptChip['conceptChipType'] // 유형칩type (개념/기본/심화)
    recommended: ConceptChipApi.Response.ConceptChip['recommended'] // 추천 여부
    bigChapterId: ConceptChipApi.Response.ConceptChip['bigChapterId'] // 대단원id
    bigChapterName: ConceptChipApi.Response.ConceptChip['bigChapterName'] // 대단원명
    middleChapterId: ConceptChipApi.Response.ConceptChip['middleChapterId'] // 중단원id
    middleChapterName: ConceptChipApi.Response.ConceptChip['middleChapterName'] // 중단원명
    littleChapterId: ConceptChipApi.Response.ConceptChip['littleChapterId'] // 소단원id
    littleChapterName: ConceptChipApi.Response.ConceptChip['littleChapterName'] // 소단원명
    conceptId: ConceptChipApi.Response.ConceptChip['conceptId'] // 유형id
    conceptName: ConceptChipApi.Response.ConceptChip['conceptName'] // 유형명
    repProblem: ConceptChipApi.Response.ConceptChip['repProblem'] | null // 대표 문제

    achievementLevel: ConceptChipDomain.LevelOfAchievement // 성취도 등급

    studentId?: StudentEntity.Student['id'] // 학생id
    latestStudyLog?: ConceptChipApi.Response.LatestStudyLog // 가장 최근 학습 이력

    constructor(data: Omit<AchievementConceptChip, 'koAchievementLevel'>) {
      this.id = data.id
      this.orderingNumber = data.orderingNumber
      this.conceptChipId = data.conceptChipId
      this.conceptChipType = data.conceptChipType
      this.recommended = data.recommended
      this.bigChapterId = data.bigChapterId
      this.bigChapterName = data.bigChapterName
      this.middleChapterId = data.middleChapterId
      this.middleChapterName = data.middleChapterName
      this.littleChapterId = data.littleChapterId
      this.littleChapterName = data.littleChapterName
      this.conceptId = data.conceptId
      this.conceptName = data.conceptName
      this.repProblem = data.repProblem
      this.achievementLevel = data.achievementLevel
      this.studentId = data.studentId
      this.latestStudyLog = data.latestStudyLog
    }

    get koAchievementLevel() {
      return ACHIEVEMENT_LEVEL_KO_TEXT_DICT[this.achievementLevel]
    }
  }

  export class GroupAchievementConceptChipDetail {
    averageCorrectRate: ConceptChipApi.Response.GroupConceptChipDetail['averageCorrectRate']
    highestCorrectRateProblems: ConceptChipApi.Response.GroupConceptChipDetail['highestCorrectRateProblems']
    highestWrongRateProblems: ConceptChipApi.Response.GroupConceptChipDetail['highestWrongRateProblems']
    averageAchievementLevel: ConceptChipApi.Response.GroupConceptChipDetail['averageAchievementLevel']
    achievements: {
      studentId: StudentEntity.Student['id']
      studentName: StudentEntity.Student['name']
      achievementLevel: ConceptChipDomain.LevelOfAchievement
    }[]

    constructor(data: ConceptChipApi.Response.GroupConceptChipDetail) {
      this.averageCorrectRate = data.averageCorrectRate
      this.highestCorrectRateProblems = data.highestCorrectRateProblems
      this.highestWrongRateProblems = data.highestWrongRateProblems
      this.averageAchievementLevel = data.averageAchievementLevel
      this.achievements = data.achievements
    }

    get achievementsGroupedByLevel() {
      return groupBy(this.achievements, 'achievementLevel') as Record<
        ConceptChipDomain.LevelOfAchievement,
        ConceptChipApi.Response.GroupConceptChipDetail['achievements']
      >
    }
  }
}
